/* Ties the phone image strip to its thumbnail row: tapping a thumbnail glides
   the strip to that picture, and swiping the strip moves the highlight to match.
   Desktop shows every image in a grid at once, so none of this runs there.

   Three things here are deliberate and worth keeping:

   1. Everything is delegated from the document rather than bound per button.
      The theme swaps page content for view transitions, so listeners attached
      at first paint can end up on nodes no longer in the page.

   2. The glide is animated by hand instead of scrollTo({behavior:'smooth'}).
      Native smooth scrolling is cancelled when it starts from inside a click —
      measured on this page, an instant scroll works while an identical smooth
      one never moves. Driving it per frame also fixes the pace, which is the
      point: a slow, eased slide rather than a jump.

   3. Snapping is switched off for the length of that animation. Mandatory snap
      pulls the strip back to the nearest image on every frame we set, so the
      two fight and the strip never arrives.

   Positions come from bounding rectangles, not offsetLeft: the wrappers between
   the strip and its figures are display:contents, which leaves offsetParent
   outside the scroller and makes offsetLeft unrelated to the scroll offset. */

const PHONE = '(max-width: 900px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const GLIDE_MS = 520;

function offsetWithin(scroller, element) {
  return element.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
}

function partsFor(node) {
  const page = node.closest('[data-product-page]');
  if (!page) return null;
  const stage = page.querySelector('.bwl-product-stage');
  const thumbs = page.querySelector('[data-product-thumbs]');
  if (!stage || !thumbs) return null;
  const slides = [...stage.querySelectorAll('.bwl-product-gallery__item')];
  const buttons = [...thumbs.querySelectorAll('[data-thumb]')];
  if (slides.length < 2 || slides.length !== buttons.length) return null;
  return { stage, thumbs, slides, buttons };
}

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function glide(stage, to) {
  const from = stage.scrollLeft;
  const distance = to - from;
  if (Math.abs(distance) < 1) return;

  if (window.matchMedia(REDUCED_MOTION).matches) {
    stage.scrollLeft = to;
    return;
  }

  const restore = stage.style.scrollSnapType;
  stage.style.scrollSnapType = 'none';
  const started = performance.now();

  const step = (now) => {
    const progress = Math.min(1, (now - started) / GLIDE_MS);
    stage.scrollLeft = from + distance * easeInOutCubic(progress);
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      stage.style.scrollSnapType = restore;
    }
  };
  requestAnimationFrame(step);
}

function mark(parts, index) {
  const { thumbs, buttons } = parts;
  let changed = false;
  buttons.forEach((b, i) => {
    const next = i === index ? 'true' : 'false';
    if (b.getAttribute('aria-current') !== next) changed = true;
    b.setAttribute('aria-current', next);
  });
  if (!changed) return;
  const active = buttons[index];
  if (!active) return;
  // Keep the active thumbnail in view without disturbing the page scroll.
  const start = offsetWithin(thumbs, active);
  if (start < thumbs.scrollLeft || start + active.offsetWidth > thumbs.scrollLeft + thumbs.clientWidth) {
    thumbs.scrollTo({ left: start - thumbs.clientWidth / 2 + active.offsetWidth / 2, behavior: 'smooth' });
  }
}

function nearestSlide(parts) {
  const { stage, slides } = parts;
  const centre = stage.scrollLeft + stage.clientWidth / 2;
  let best = 0;
  let bestDistance = Infinity;
  slides.forEach((slide, i) => {
    const middle = offsetWithin(stage, slide) + slide.getBoundingClientRect().width / 2;
    const distance = Math.abs(middle - centre);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  });
  return best;
}

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-thumb]');
  if (!button) return;
  if (!window.matchMedia(PHONE).matches) return;
  const parts = partsFor(button);
  if (!parts) return;
  const index = parts.buttons.indexOf(button);
  const slide = parts.slides[index];
  if (!slide) return;
  const left = offsetWithin(parts.stage, slide) - (parts.stage.clientWidth - slide.getBoundingClientRect().width) / 2;
  glide(parts.stage, left);
  mark(parts, index);
});

// Scroll does not bubble, so this listens in the capture phase instead.
let pending = false;
document.addEventListener(
  'scroll',
  (event) => {
    const stage = event.target;
    if (!stage.classList?.contains('bwl-product-stage')) return;
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      const parts = partsFor(stage);
      if (parts) mark(parts, nearestSlide(parts));
    });
  },
  { capture: true, passive: true }
);
